import { Observable, takeWhile, tap, timer } from "rxjs";

import {
  BookingsPollingStatusEnum,
  IBookingsPollingCombinedState,
  Log,
} from "@ryde/shared";

interface ICreateBookingsPollingStreamArguments {
  intervalMs: number;
  isRunning: () => boolean;
  onTick: (pollingCount: number) => void;
}

/**
 * Ticks while the run is still going. `isRunning` is a callback, not a value: captured as a value it
 * freezes at subscribe time and the stream stops after one tick. Order matches the original - the
 * tick sends its check before the stop decision, so the last state is always fetched.
 */
export const createBookingsPollingStream = ({
  intervalMs,
  isRunning,
  onTick,
}: ICreateBookingsPollingStreamArguments): Observable<number> =>
  timer(intervalMs, intervalMs).pipe(
    tap(pollingCount => onTick(pollingCount)),
    takeWhile(() => isRunning()),
  );

export const isBookingsPollingStatusRunning = (
  state: IBookingsPollingCombinedState | undefined,
): boolean => {
  if (
    state &&
    (state?.status === BookingsPollingStatusEnum.Initializing ||
      state?.status === BookingsPollingStatusEnum.InProgress)
  ) {
    return true;
  }

  return false;
};

export const isBookingsPollingCurrentStateNotRunning = (
  state: IBookingsPollingCombinedState | undefined,
): boolean => {
  if (state?.status === BookingsPollingStatusEnum.ConflictError) {
    return false;
  }

  if (state?.status && !isBookingsPollingStatusRunning(state)) {
    const isFinished = state?.status === BookingsPollingStatusEnum.Completed;

    if (isFinished) {
      Log.debug("Production finished", state);
    } else {
      Log.debug("Not running state received", state);
    }

    return true;
  }

  return false;
};
